import React, { useRef, useEffect } from 'react';
import { useIntl, defineMessages } from 'react-intl-next';

import { token } from '@atlaskit/tokens';
import CommentAddIcon from '@atlaskit/icon/core/comment-add';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss, Box, Pressable, Flex, Inline } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';

import { fg } from '@confluence/feature-gating';
import { AddCommentPromptButton } from '@confluence/comments-common/entry-points/AddCommentPromptButton';
import { useAddCommentPermissionCheck } from '@confluence/comments-hooks/entry-points/useAddCommentPermissionCheck';
import { usePageContentId } from '@confluence/page-context';
import { expVal, expValEquals } from '@confluence/feature-experiments';
import {
	ShortcutVisualizer,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer';
import { UNIVERSAL_OPEN_COMMENT_EDITOR } from '@confluence/shortcuts/entry-points/confluence-shortcuts';

const addCommentButtonStyle = xcss({
	display: 'flex',
	minHeight: '32px',
	padding: 'space.0',
	paddingLeft: 'space.150',
	paddingRight: 'space.150',
	justifyContent: 'center',
	alignItems: 'center',
	borderRadius: 'radius.small',
	font: token('font.body'),
});

const enabledStyle = xcss({
	backgroundColor: 'color.background.neutral.subtle',
	':hover': {
		cursor: 'pointer',
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
	':active': {
		backgroundColor: 'color.background.neutral.subtle.pressed',
	},
	color: 'color.text.subtle',
});

const disabledStyle = xcss({
	backgroundColor: 'color.background.disabled',
	cursor: 'not-allowed',
	color: 'color.text.disabled',
});

const i18n = defineMessages({
	addCommentPromptText: {
		id: 'object-comments.add-comment-prompt.text',
		defaultMessage: 'Add a comment',
		description: 'Label for the add comment prompt',
	},
	addCommentPromptAriaLabel: {
		id: 'object-comments.add-comment-prompt.aria-label',
		defaultMessage: 'Add a general comment',
		description: 'The aria label for the add general comment entry prompt field',
	},
	addCommentTooltip: {
		id: 'object-comments.footer.add-button-tooltip',
		defaultMessage: 'Add comment',
		description:
			'Tooltip message for adding a comment, used in conjunction with shortcut visualizer',
	},
});

export const ObjectAddCommentPrompt = ({
	onClick,
	autofocusRef,
}: {
	onClick: () => void;
	autofocusRef: { current: boolean | null };
}) => {
	const { formatMessage } = useIntl();
	const [contentId] = usePageContentId();

	const { canAddComments } = useAddCommentPermissionCheck(contentId || '');

	const addCommentShortcutStrings = useShortcutStrings({
		shortcut: UNIVERSAL_OPEN_COMMENT_EDITOR,
	});

	const buttonRef = useRef<HTMLButtonElement>(null);

	useEffect(() => {
		if (autofocusRef.current) {
			(buttonRef.current as HTMLElement)?.focus({
				preventScroll: fg('confluence_comment_prevent_focus_autoscroll'),
			});
			autofocusRef.current = false;
		}
	}, [autofocusRef]);

	const allowAddComment = fg('confluence_frontend_content_wrapper') ? canAddComments : true;

	// When the FG platform_tooltip_shortcuts_migration_1 is enabled, the shortcut is placed in the `shortcut` Tooltip prop
	const tooltipContent = fg('platform_tooltip_shortcuts_migration_1') ? (
		formatMessage(i18n.addCommentTooltip)
	) : (
		<ShortcutVisualizer
			shortcut={UNIVERSAL_OPEN_COMMENT_EDITOR}
			contentBefore={formatMessage(i18n.addCommentTooltip)}
			isTooltip
		/>
	);

	return (
		<Box>
			<Tooltip
				content={
					expValEquals('confluence_comments_cohiti_footer_tooltips', 'isEnabled', true)
						? tooltipContent
						: undefined
				}
				shortcut={
					fg('platform_tooltip_shortcuts_migration_1') ? addCommentShortcutStrings : undefined
				}
				position="top"
			>
				{expVal('cc_comments_refactor_add_comment_button', 'isEnabled', false) ? (
					<AddCommentPromptButton
						onClick={onClick}
						autofocusRef={autofocusRef}
						testId="add-comment-button"
					/>
				) : (
					<Pressable
						xcss={[addCommentButtonStyle, !allowAddComment ? disabledStyle : enabledStyle]}
						isDisabled={!allowAddComment}
						onClick={onClick}
						testId="add-comment-button"
						ref={buttonRef}
						// TODO - COMMENTS-4934: Clean up confluence_frontend_comments_a11y_kb FG
						{...(fg('confluence_frontend_comments_a11y_kb')
							? {
									'aria-label': formatMessage(i18n.addCommentPromptAriaLabel),
								}
							: {})}
					>
						<Flex alignItems="center" gap="space.100">
							<CommentAddIcon label={formatMessage(i18n.addCommentPromptText)} />
							<Inline>{formatMessage(i18n.addCommentPromptText)}</Inline>
						</Flex>
					</Pressable>
				)}
			</Tooltip>
		</Box>
	);
};
